import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useT } from "@/lib/LanguageContext";
import { tenantHref } from "@/lib/tenantPath";
import ThemedPageLayout from "@/templates/ThemedPageLayout";
// Relative on purpose: pure modules, resolvable under Jest without a shim.
import { unsubscribeNudges } from "../../pathways/learnerApi";
import { pathwayErrorCode, pathwayErrorMessage } from "../../pathways/errors";
import { PATHWAYS_LEARNER as IDS } from "../../constants/testIds/pathwaysLearner";

/**
 * `/parcours/nudges-off?token=…`: the unsubscribe link of the reminder e-mail
 * (`pathway_nudge_client`, contract §6). The token is POSTed on arrival
 * (`POST /public/pathways/nudges/unsubscribe`, contract §5.2); no session is needed, the
 * token is the proof.
 *
 *   working   "Turning reminders off…"
 *   done      "You won't receive reminders for <pathway_title> any more."
 *   invalid   400 TOKEN_INVALID, or a link without a token
 *   error     anything else, with a retry (a network failure is not an invalid link)
 *
 * The POST is safe to repeat (it sets `nudges_opted_out`), so a double mount under
 * StrictMode, or a retry, does no harm.
 */
export default function NudgesOff() {
  const t = useT("public");
  const tc = useT("common");
  const { search } = useLocation();
  const token = new URLSearchParams(search).get("token") || "";
  const [state, setState] = useState(token ? { kind: "working" } : { kind: "invalid", error: null });

  const run = useCallback(async (alive = () => true) => {
    setState({ kind: "working" });
    try {
      const result = await unsubscribeNudges(token);
      if (alive()) setState({ kind: "done", title: result.pathway_title });
    } catch (e) {
      if (!alive()) return;
      if (pathwayErrorCode(e) === "TOKEN_INVALID") {
        setState({ kind: "invalid", error: e });
      } else {
        setState({ kind: "error" });
      }
    }
  }, [token]);

  useEffect(() => {
    if (!token) return undefined;
    let alive = true;
    run(() => alive);
    return () => { alive = false; };
  }, [token, run]);

  return (
    <ThemedPageLayout>
      <section className="pt-32 pb-24 px-4 sm:px-6">
        <div data-testid={IDS.nudgesOff} className="mx-auto max-w-xl rounded-3xl border border-eh-border-soft bg-eh-cream px-6 py-12 text-center">
          <h1 className="font-display text-3xl text-eh-ink">{t("pathways.nudges.title")}</h1>
          {state.kind === "working" && (
            <p role="status" className="mt-4 inline-flex items-center gap-2 text-eh-muted">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> {t("pathways.nudges.working")}
            </p>
          )}
          {state.kind === "done" && (
            <p role="status" data-testid={IDS.nudgesOffDone} className="mt-4 text-eh-ink break-words">
              {t("pathways.nudges.done", { title: state.title })}
            </p>
          )}
          {state.kind === "invalid" && (
            <p role="alert" data-testid={IDS.nudgesOffInvalid} className="mt-4 text-eh-ink">
              {state.error ? pathwayErrorMessage(state.error, tc, t("pathways.nudges.invalid")) : t("pathways.nudges.invalid")}
            </p>
          )}
          {state.kind === "error" && (
            <div data-testid={IDS.nudgesOffError} className="mt-4 space-y-4">
              <p role="alert" className="text-eh-ink">{t("pathways.nudges.error")}</p>
              <button
                type="button"
                onClick={() => run()}
                className="inline-flex h-11 items-center rounded-full bg-eh-ink px-6 text-sm font-medium text-eh-cream"
              >
                {t("pathways.retry")}
              </button>
            </div>
          )}
          <a href={tenantHref("/compte")} className="mt-8 inline-block text-sm font-medium text-eh-ink underline">
            {t("pathways.nudges.account")}
          </a>
        </div>
      </section>
    </ThemedPageLayout>
  );
}
